import { useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../../api/http";
import { useConfirm, usePrompt } from "../../../components/Dialog";
import {
  IconCard,
  IconClipboardList,
  IconDollar,
  IconPause,
  IconPlay,
} from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import { SubscriptionListRow } from "../../../types/subscriptions";
import PaymentMethodsDrawer from "../../SubscriptionDetail/components/PaymentMethodsDrawer";

const STRIPE_DASHBOARD = "https://dashboard.stripe.com/customers/";

type Props = {
  row: SubscriptionListRow;
  onAfterChange: () => void;
};

type Busy = null | "pause" | "resume";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function SubscriptionActions({ row, onAfterChange }: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [pmDrawerOpen, setPmDrawerOpen] = useState(false);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const handlePause = async () => {
    const date = await prompt({
      title: "Pausar suscripción",
      message: `Suscripción ${row.subscriptionId}. Selecciona la fecha de pausa.`,
      defaultValue: todayISO(),
      type: "date",
      okLabel: "Pausar",
      tone: "warning",
      validate: (v) =>
        /^\d{4}-\d{2}-\d{2}$/.test(v) ? null : "Formato esperado: YYYY-MM-DD",
    });
    if (!date) return;
    setBusy("pause");
    setError(null);
    try {
      await request(
        `/api/admin/operations/subscriptions/${row.subscriptionId}/pause`,
        { method: "POST", token, body: { pauseDate: date } },
      );
      onAfterChange();
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo pausar");
    } finally {
      setBusy(null);
    }
  };

  const handleResume = async () => {
    const ok = await confirm({
      title: "Reactivar suscripción",
      message: `Reactivar la suscripción ${row.subscriptionId}.`,
      confirmLabel: "Reactivar",
      tone: "primary",
    });
    if (!ok) return;
    setBusy("resume");
    setError(null);
    try {
      await request(
        `/api/admin/operations/subscriptions/${row.subscriptionId}/resume`,
        { method: "POST", token, body: {} },
      );
      onAfterChange();
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo reactivar");
    } finally {
      setBusy(null);
    }
  };

  const isPaused = row.status === "PAUSED";

  return (
    <div
      style={{
        display: "flex",
        gap: "0.3rem",
        justifyContent: "flex-end",
        position: "relative",
      }}
      onClick={stop}
    >
      <Link
        to={`/subscriptions/${row.subscriptionId}`}
        title="Abrir detalle de la suscripción"
        style={iconBtnStyle}
      >
        <IconClipboardList />
      </Link>
      <a
        href={
          row.stripeCustomerId ? `${STRIPE_DASHBOARD}${row.stripeCustomerId}` : "#"
        }
        target="_blank"
        rel="noopener noreferrer"
        title={
          row.stripeCustomerId
            ? `Abrir ${row.stripeCustomerId} en Stripe`
            : "Sin Stripe customer ID"
        }
        aria-disabled={!row.stripeCustomerId}
        style={{
          ...iconBtnStyle,
          opacity: row.stripeCustomerId ? 1 : 0.4,
          pointerEvents: row.stripeCustomerId ? "auto" : "none",
        }}
      >
        <IconDollar />
      </a>
      <button
        type="button"
        onClick={() => setPmDrawerOpen(true)}
        title="Métodos de pago"
        style={iconBtnStyle}
      >
        <IconCard />
      </button>
      <button
        type="button"
        onClick={isPaused ? handleResume : handlePause}
        disabled={busy !== null}
        title={isPaused ? "Reactivar suscripción" : "Pausar suscripción"}
        style={{
          ...iconBtnStyle,
          color: isPaused ? "#1a7f37" : "#856404",
          background: isPaused ? "#d4edda" : "#fff3cd",
          borderColor: isPaused ? "#a4d4b1" : "#ffe69c",
          opacity: busy !== null ? 0.6 : 1,
          cursor: busy !== null ? "wait" : "pointer",
        }}
      >
        {isPaused ? <IconPlay /> : <IconPause />}
      </button>
      {pmDrawerOpen && (
        <PaymentMethodsDrawer
          subscriptionId={row.subscriptionId}
          leadUlid={row.leadUlid}
          currentDefault={row.defaultPaymentMethod ?? ""}
          onClose={() => setPmDrawerOpen(false)}
          onChanged={onAfterChange}
        />
      )}
      {error && (
        <span
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 30,
            color: "#b32424",
            background: "#fde2e2",
            padding: "0.35rem 0.6rem",
            borderRadius: "0.35rem",
            fontSize: ".72rem",
            cursor: "pointer",
            whiteSpace: "normal",
          }}
          onClick={() => setError(null)}
          title="Cerrar"
        >
          {error}
        </span>
      )}
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "1.9rem",
  height: "1.9rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  fontSize: ".95rem",
  cursor: "pointer",
  textDecoration: "none",
};
